import { useLayoutEffect, useMemo, useRef } from 'react';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { typography, semantic, radius } from '@/shared/theme/tokens';
import { useFurnaceSvg } from '../../hooks/useFurnaceSvg';
import { Sparkline } from '../../ui/Sparkline';
import { LIVE_STATUS_META } from './consensusMeta';

const STATUS_RANK = { critical: 0, warning: 1, ok: 2 };

// One callout chip per parameter, worst-status first, capped to keep the hero
// readable. De-duped by parameter name.
function pickCallouts(params, max = 3) {
  const byName = new Map();
  for (const p of params) {
    if (p.isStale) continue; // never float a stale value as a live reading
    const cur = byName.get(p.parameterName);
    if (!cur || STATUS_RANK[p.status] < STATUS_RANK[cur.status]) byName.set(p.parameterName, p);
  }
  return [...byName.values()]
    .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status])
    .slice(0, max);
}

/**
 * The furnace hero: the plant schematic as a live backdrop with floating value
 * callouts. "Show the photo of the furnace first" (CEO). The callouts read
 * their live values from the simulator.
 */
export function FurnaceHero({ svgUrl, params = [], animate }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const hostRef = useRef(null);
  const { data: markup, isLoading, isError } = useFurnaceSvg(svgUrl);

  const callouts = useMemo(() => pickCallouts(params), [params]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || !markup) return;
    host.innerHTML = markup;
    const svg = host.querySelector('svg');
    if (svg) {
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.style.display = 'block';
      svg.style.maxHeight = '100%';
    }
  }, [markup]);

  return (
    <div style={{
      position: 'relative',
      flex: 1,
      minHeight: 0,
      borderRadius: radius['2xl'],
      overflow: 'hidden',
      background: isDark
        ? 'radial-gradient(circle at 50% 30%, #1c2740, #11151f 70%)'
        : 'radial-gradient(circle at 50% 30%, #ffffff, #eef2fa 75%)',
      border: `1px solid ${c.border}`,
    }}>
      {/* Header chip */}
      <div style={{
        position: 'absolute', top: 12, left: 14, zIndex: 2,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: semantic.brandAccent }}>
          factory
        </span>
        <span style={{
          fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.bold, color: c.textPrimary, letterSpacing: typography.letterSpacing.wide,
        }}>
          Furnace — Live
        </span>
      </div>

      {/* SVG backdrop */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '44px 16px 16px',
        filter: isDark ? 'brightness(0.92) contrast(1.05)' : 'none',
      }}>
        {isLoading && (
          <span style={{ fontSize: typography.fontSize.md, color: c.textSubtle }}>Loading furnace…</span>
        )}
        {isError && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 36, color: semantic.error }}>broken_image</span>
            <span style={{ fontSize: typography.fontSize.md, color: c.textSubtle }}>Furnace diagram unavailable</span>
          </div>
        )}
        {!isLoading && !isError && (
          <div ref={hostRef} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
        )}
      </div>

      {/* Healthy state — no active-alert callouts to show */}
      {!isLoading && !isError && callouts.length === 0 && (
        <div style={{
          position: 'absolute', top: 44, right: 14, zIndex: 2,
          display: 'flex', alignItems: 'center', gap: 8,
          background: isDark ? 'rgba(20,25,39,0.82)' : 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(4px)',
          border: `1px solid ${LIVE_STATUS_META.ok.border}`,
          borderLeft: `3px solid ${LIVE_STATUS_META.ok.color}`,
          borderRadius: radius.lg, padding: '8px 12px',
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: LIVE_STATUS_META.ok.color }}>check_circle</span>
          <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>All systems nominal</span>
        </div>
      )}

      {/* Floating live value callouts */}
      <div style={{
        position: 'absolute', top: 44, right: 14, bottom: 14, zIndex: 2,
        display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'flex-start',
        width: 188, pointerEvents: 'none',
      }}>
        {callouts.map((p) => {
          const meta = LIVE_STATUS_META[p.status] ?? LIVE_STATUS_META.ok;
          return (
            <div key={p.id} style={{
              background: isDark ? 'rgba(20,25,39,0.82)' : 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(4px)',
              border: `1px solid ${meta.border}`,
              borderLeft: `3px solid ${meta.color}`,
              borderRadius: radius.lg,
              padding: '8px 10px',
              boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.4)' : '0 4px 14px rgba(30,67,150,0.10)',
            }}>
              <div style={{
                fontSize: typography.fontSize.xs, color: c.textSubtle,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wide,
              }}>
                {p.parameterName}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
                <span style={{
                  fontSize: typography.fontSize['3xl'], fontWeight: typography.fontWeight.bold, color: meta.color,
                  fontVariantNumeric: 'tabular-nums', transition: animate ? 'color 0.4s' : 'none',
                }}>
                  {p.liveValue}
                </span>
                {p.unit && <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{p.unit}</span>}
              </div>
              <div style={{ marginTop: 2, opacity: 0.9 }}>
                <Sparkline data={p.buffer} color={meta.color} height={22} animate={false} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
